import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { DriverApi } from "../driver-api.ts";
import {
  bannerFor,
  chipFor,
  itemLabel,
  noteFor,
  savedLabel,
  sendQueue,
  wordingIsDesigned,
  type QueueCondition,
  type QueueStore,
  type QueuedItem,
} from "../offline-queue.ts";
import { PhoneShell, type RowProps } from "./PhoneShell.tsx";

/**
 * `6 Offline queue` — _"Keep going. We'll sync later."_
 *
 * The design: an amber `cloud_off` strip reading `Offline · 3 items waiting to sync`, a map hero, the
 * dashed `pending sync` pill, _"Scans and proof are saved on this phone and sent in order."_, three
 * key/value rows naming what is waiting and when this phone saved it, the dashed note _"Duplicates
 * are fine and ignored. Only a gap in the sequence is a problem, and we'll tell you."_, and one
 * 56dp `Next stop`.
 *
 * `tests/contract/mobile-screen-support.test.ts` marks this screen **buildable with nothing
 * missing**, and the last absence it lost was FIL-02's public upload route — published on `/v1` by
 * [ADR 0025](../../../docs/adr/0025-the-upload-service-gets-a-public-surface-and-its-own-device-scope.md).
 * Before that, the queued photograph the design draws had nowhere to go, so the screen could show a
 * list and never empty it.
 *
 * ## The queue is this phone's, and that is the screen's subject
 *
 * There is no read behind this screen and there could not be: the queue exists precisely because the
 * platform has not been told about any of it. So `offline-queue.ts` owns the store and the sending,
 * and this component does three things — draw what is waiting, send it in order, and say what
 * happened. Nothing here invents a platform field; the rows are the device's own records and the two
 * destinations are LOG-06 and FIL-02.
 *
 * ## What fills it, today
 *
 * Nothing in this build. A queue entry is made where a capture is made, and the two screens that
 * capture — `5 Scan` and `Document upload` — are not built: `5 Scan` has no parcel read, and
 * `Document upload` is blocked on FIL-03's internal job read and is deliberately not built, because
 * a screen built against a route that does not exist is worse than no screen. So a courier opening
 * this screen today sees the empty state, which is the true one. Recorded as R-33 and Q-72; the
 * store is public so the producers can use it the moment they exist, and
 * `tooling/design-fidelity/fixtures/driver-app.mts` seeds it to the design's own three samples for
 * the comparison.
 */

export interface OfflineQueueProps {
  readonly api: DriverApi;
  readonly tenantId: string;
  /** Where the queue lives. Injected so a test and the acceptance check can state one. */
  readonly store: QueueStore;
  /** The token's scopes, so an attempt that cannot succeed is not made and is named instead. */
  readonly scopes: ReadonlySet<string>;
  /**
   * The courier, where the token says so.
   *
   * Only used to attach a completed upload to a DRV-03 document. Absent when the realm issues no
   * `driver_id` claim, and then the upload still completes and is simply not attached — a document
   * written against a guessed `driverId` would be a permanent record about somebody else, which is
   * why R-29 refuses the guess for every read and refuses it harder here.
   */
  readonly driverId?: string | undefined;
}

export function OfflineQueue(props: OfflineQueueProps): ReactNode {
  const { api, tenantId, store, scopes, driverId } = props;

  const [items, setItems] = useState<readonly QueuedItem[]>(() => store.list());
  const [condition, setCondition] = useState<QueueCondition>(() =>
    store.list().length === 0 ? { kind: "empty" } : { kind: "sending" },
  );

  /**
   * One pass over the queue.
   *
   * Not on a timer. The design's screen is a courier with no signal, and a retry loop against a dead
   * connection is a loop that drains a battery in a car park for no benefit — the browser already
   * says when the signal comes back, and the `online` event below is that. A pass is therefore made
   * when this screen opens and whenever the browser reports a connection; everything else is the
   * courier closing and reopening the application, which is the thing a phone does anyway.
   */
  const run = useCallback(() => {
    const waiting = store.list();
    setItems(waiting);
    if (waiting.length === 0) {
      setCondition({ kind: "empty" });
      return;
    }
    setCondition({ kind: "sending" });
    void sendQueue({
      items: waiting,
      api,
      tenantId,
      bytes: (id) => store.bytes(id),
      scopes,
      ...(driverId === undefined ? {} : { driverId }),
    }).then((report) => {
      /*
       * The store is written from the report rather than from this component's state.
       *
       * `report.remaining` carries the records the pass updated — an upload that got as far as
       * being minted keeps its `uploadId`, so the next pass completes it instead of initiating a
       * second one. Dropping that would make every interrupted upload a new object in quarantine.
       */
      store.replace(report.remaining);
      setItems(report.remaining);
      setCondition(report.condition);
    });
  }, [api, tenantId, store, scopes]);

  useEffect(run, [run]);

  useEffect(() => {
    window.addEventListener("online", run);
    return (): void => window.removeEventListener("online", run);
  }, [run]);

  const rows: readonly RowProps[] = useMemo(
    () =>
      items.map((item) => {
        const saved = savedLabel(item.savedAt);
        return {
          key: itemLabel(item),
          value: saved,
          ...(saved === null ? { because: "This phone recorded no time for this capture" } : {}),
        };
      }),
    [items],
  );

  const banner = bannerFor(condition, items.length);
  const note = noteFor(condition);

  return (
    <PhoneShell
      screen="offline-queue"
      /*
       * `map:1` in the design, which sets the hero's height to 170 — `d.rows.length > 2` — and its
       * background to `C.map`. Nothing is drawn in it, for the reason `PhoneShell`'s own closing note
       * gives: the design builds five map layers for its four map screens and assigns `layers` twice,
       * so none of them reaches the page in the design file or in the approved reference screenshot.
       * Q-68.
       */
      map
      {...(banner === undefined ? {} : { banner })}
      /*
       * `on:1`, the design's own flag, with no presence read behind it — the same reading the three
       * offer screens take. This screen composes nothing from DRV-05 and adding a presence read for
       * a pill would be a second direct domain read for decoration.
       */
      online
      earningsLabel={null}
      chip={chipFor(condition)}
      title={title(condition)}
      sub={sub(condition)}
      rows={rows}
      note={note}
      actions={[
        {
          label: "Next stop",
          glyph: "arrow_forward",
          primary: true,
          /*
           * Drawn, because the design draws it, and honest about why it cannot move.
           *
           * `Next stop` needs the stop after this one, and the stop plan a courier is carrying is
           * the absence `Multi-stop job` is blocked on: `POST /v1/tenants/{tenantId}/stop-plans`
           * exists and there is no read. Nothing is removed and nothing pretends.
           */
          unavailable:
            "Nothing publishes the stop after this one: POST /v1/tenants/{tenantId}/stop-plans exists and there is no GET.",
        },
      ]}
      {...(wordingIsDesigned(condition) ? {} : { flag: "wording not in the design" })}
    />
  );
}

/**
 * The headline.
 *
 * _"Keep going. We'll sync later."_ is the design's and is kept on every state where it is still
 * true — which is every state with something waiting, including the two that have gone wrong,
 * because in all of them the courier should carry on and nothing has been lost. An empty queue is the
 * one state where that sentence would be a non-sequitur.
 */
function title(condition: QueueCondition): string {
  return condition.kind === "empty" ? "Everything's been sent" : "Keep going. We'll sync later.";
}

/** The sub-line. The design's, except where the design's would be untrue. */
function sub(condition: QueueCondition): string {
  switch (condition.kind) {
    case "empty":
      return "Nothing is waiting on this phone. Scans and proof are sent as you capture them.";
    case "gap":
    case "held":
    case "forbidden":
    case "refused":
      // Still saved on this phone, and still in order — but no longer "sent", so the design's verb
      // would be a promise this build cannot keep until the note's reason is dealt with.
      return "Scans and proof are saved on this phone. Nothing has been lost.";
    case "queued":
    case "sending":
      return "Scans and proof are saved on this phone and sent in order.";
  }
}
